/**
 * useBank — React hook for the player's bank vault state + actions.
 *
 * Connects to the authoritative Colyseus bank protocol via useColyseusMessages
 * and NetworkManager room messaging, with fallback to local BankSystem for
 * offline/test fixtures.
 *
 * < 500 LOC decomposition invariant strictly preserved.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BankSystem } from '../../systems/inventory/BankSystem';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { useColyseusMessages } from './useColyseusMessages';
import { gameLogger } from '../../../utils/GameLogger';
import type { InventoryItem } from '../../../shared/CharacterTypes';
import type { BankUpdateMessage } from '../../network/ClientMessageTypes';
import { BANK_TEST_GEAR_MESSAGE } from '../../../shared/qa/BankTestGear';


/** Does an authoritative server bank protocol exist? */
export const HAS_AUTHORITATIVE_BANK_PROTOCOL: boolean = true;

/** Machine-readable reason the Bank cannot be actioned. */
export type BankUnavailableReason = 'no-server-protocol' | 'no-local-producer';

export interface BankAvailability {
  /** True only when a deposit/withdraw can actually be honoured. */
  available: boolean;
  /** Why `available` is false; `null` when available. */
  unavailableReason: BankUnavailableReason | null;
}

function getActiveRoom(): any | null {
  const direct = (globalThis as any).nm?.getRoom?.();
  if (direct) return direct;
  try {
    return ServiceRegistry.getInstance().get('NetworkManager', { optional: true })?.getRoom?.() ?? null;
  } catch {
    return null;
  }
}

/**
 * Pure availability rule. The missing server protocol is the structural
 * reason and is reported first — a local producer cannot make an unbacked
 * vault authoritative.
 */
export function resolveBankAvailability(input: {
  hasServerProtocol: boolean;
  hasLocalProducer: boolean;
}): BankAvailability {
  if (!input.hasServerProtocol) {
    return { available: false, unavailableReason: 'no-server-protocol' };
  }
  if (!input.hasLocalProducer) {
    return { available: false, unavailableReason: 'no-local-producer' };
  }
  return { available: true, unavailableReason: null };
}

/** Default bank vault capacity (Character.ts:59, PlayerStateBroadcaster.ts:247). */
export const DEFAULT_BANK_MAX_SLOTS = 100;

/** The server's last answer to a gold transfer (bankSuccess / bankError). */
export interface BankGoldNotice {
  action: 'depositGold' | 'withdrawGold';
  tone: 'ok' | 'error';
  text: string;
}

export interface BankState extends BankAvailability {
  bankItems: InventoryItem[];
  bankGold: number;
  bankMaxSlots: number;
  /** The last gold transfer's answer, shown under that purse. Optional for fixtures. */
  goldNotice?: BankGoldNotice | null;
}

const GOLD_ACTIONS = new Set(['depositGold', 'withdrawGold']);

/** Turn a bankSuccess / bankError message into a purse notice; null when it is not about gold. */
export function goldNoticeFrom(kind: 'success' | 'error', msg: unknown): BankGoldNotice | null {
  const { action, amount, reason } = (msg ?? {}) as { action?: unknown; amount?: unknown; reason?: unknown };
  if (typeof action !== 'string' || !GOLD_ACTIONS.has(action)) return null;
  const goldAction = action as BankGoldNotice['action'];
  if (kind === 'error') {
    return { action: goldAction, tone: 'error', text: typeof reason === 'string' && reason ? reason : 'The bank refused that.' };
  }
  const count = typeof amount === 'number' && Number.isFinite(amount) ? Math.floor(amount).toLocaleString('en-US') : '';
  const verb = goldAction === 'depositGold' ? 'Deposited' : 'Withdrew';
  return { action: goldAction, tone: 'ok', text: count ? `${verb} ${count} gold.` : `${verb} gold.` };
}

export interface BankActions {
  /** Move `qty` of an item from inventory into the vault; defaults to 1. */
  deposit: (itemId: string, qty?: number) => void;
  /** Move `qty` of an item from the vault into inventory; defaults to 1. */
  withdraw: (itemId: string, qty?: number) => void;
  /** Deposit `amount` gold into the vault. */
  depositGold: (amount: number) => void;
  /** Withdraw `amount` gold from the vault. */
  withdrawGold: (amount: number) => void;
  /** Dev/QA only: request standard starter test gear pack. */
  grantTestGear: () => void;
}

export function useBank(): BankState & BankActions {
  const [bankItems, setBankItems] = useState<InventoryItem[]>([]);
  const [bankGold, setBankGold] = useState(0);
  const [bankMaxSlots, setBankMaxSlots] = useState(DEFAULT_BANK_MAX_SLOTS);
  const [goldNotice, setGoldNotice] = useState<BankGoldNotice | null>(null);

  const [activeRoom, setActiveRoom] = useState<any | null>(() => getActiveRoom());

  const hasRoom = !!activeRoom;
  const hasLocalProducer = hasRoom || !!BankSystem.getInstance();
  const hasServerProtocol = HAS_AUTHORITATIVE_BANK_PROTOCOL && hasRoom;

  // Held in a ref so the action callbacks can force a re-poll without being recreated each render.
  const pollRef = useRef<() => void>(() => {});

  useEffect(() => {
    let lastRoom = getActiveRoom();
    setActiveRoom(lastRoom);
    if (lastRoom) {
      lastRoom.send('bank:open', {});
    }

    const poll = () => {
      const current = getActiveRoom();
      if (current !== lastRoom) {
        lastRoom = current;
        setActiveRoom(current);
        if (current) {
          current.send('bank:open', {});
        }
      }
      const sys = BankSystem.getInstance();
      if (!sys) return;
      const vault = sys.getVault();
      setBankItems([...vault.items]);
      setBankGold(vault.gold);
      if (typeof (vault as any).maxSlots === 'number') {
        setBankMaxSlots((vault as any).maxSlots);
      }
    };
    pollRef.current = poll;
    poll();
    const interval = setInterval(poll, 500);
    return () => clearInterval(interval);
  }, []);

  useColyseusMessages({
    bankUpdate: (msg: BankUpdateMessage) => {
      if (!msg) return;
      setBankItems(Array.isArray(msg.items) ? (msg.items as unknown as InventoryItem[]) : []);
      setBankGold(typeof msg.gold === 'number' ? msg.gold : 0);
      if (typeof (msg as any).maxSlots === 'number' && Number.isFinite((msg as any).maxSlots) && (msg as any).maxSlots >= 0) {
        setBankMaxSlots(Math.floor((msg as any).maxSlots));
      }
    },
    bankSuccess: (msg: unknown) => {
      const notice = goldNoticeFrom('success', msg);
      if (notice) setGoldNotice(notice);
    },
    bankError: (msg: unknown) => {
      const notice = goldNoticeFrom('error', msg);
      if (notice) setGoldNotice(notice);
    },
  });

  const availability = useMemo(
    () =>
      resolveBankAvailability({
        hasServerProtocol,
        hasLocalProducer,
      }),
    [hasServerProtocol, hasLocalProducer],
  );

  const refuse = useCallback(
    (action: string): boolean => {
      if (availability.available) return false;
      gameLogger.warn(
        'bank',
        `Refused ${action}: bank is unavailable (${availability.unavailableReason}).`,
      );
      return true;
    },
    [availability],
  );

  const deposit = useCallback(
    (itemId: string, qty: number = 1) => {
      if (refuse('deposit')) return;
      const activeRoom = getActiveRoom();
      if (activeRoom) {
        activeRoom.send('bank:depositItem', { itemId, quantity: qty });
      } else {
        BankSystem.getInstance()?.depositItem(itemId, qty);
        pollRef.current();
      }
    },
    [refuse],
  );

  const withdraw = useCallback(
    (itemId: string, qty: number = 1) => {
      if (refuse('withdraw')) return;
      const activeRoom = getActiveRoom();
      if (activeRoom) {
        activeRoom.send('bank:withdrawItem', { itemId, quantity: qty });
      } else {
        BankSystem.getInstance()?.withdrawItem(itemId, qty);
        pollRef.current();
      }
    },
    [refuse],
  );

  const depositGold = useCallback(
    (amount: number) => {
      if (refuse('depositGold')) return;
      setGoldNotice(null);
      const activeRoom = getActiveRoom();
      if (activeRoom) {
        activeRoom.send('bank:depositGold', { amount });
      } else {
        BankSystem.getInstance()?.depositGold(amount);
        pollRef.current();
      }
    },
    [refuse],
  );

  const withdrawGold = useCallback(
    (amount: number) => {
      if (refuse('withdrawGold')) return;
      setGoldNotice(null);
      const activeRoom = getActiveRoom();
      if (activeRoom) {
        activeRoom.send('bank:withdrawGold', { amount });
      } else {
        BankSystem.getInstance()?.withdrawGold(amount);
        pollRef.current();
      }
    },
    [refuse],
  );

  const grantTestGear = useCallback(() => {
    const activeRoom = getActiveRoom();
    if (!activeRoom) {
      gameLogger.warn('bank', 'Cannot grant test gear: no active room connection');
      return;
    }
    activeRoom.send(BANK_TEST_GEAR_MESSAGE, {});
  }, []);

  return {
    bankItems,
    bankGold,
    bankMaxSlots,
    goldNotice,
    available: availability.available,
    unavailableReason: availability.unavailableReason,
    deposit,
    withdraw,
    depositGold,
    withdrawGold,
    grantTestGear,
  };
}
